import "./App.css";
import React, { useState, useEffect } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LanguageProvider } from "./context/LanguageContext";
import { ScrollLockProvider } from "./context/ScrollLockContext";
import NavScrollExample from "./components/Navbar";
import BookingHomePage from "./components/BookingHomePage";
import RestaurantsPage from "./components/RestaurantsPage";
import HotelRoomsPage from "./components/HotelRoomPage";
import Footer from "./components/Footer";
import ContactUs from "./components/Contact";
import AboutUs from "./components/AboutUs";
import ProfileDropdown from "./components/ProfileDropdown";
import Faqs from "./components/Faqs";
import Blog from "./components/Blog";
import Careers from "./components/Careers";
import CustomerSupport from "./components/CustomerSupport";
import TermsConditions from "./components/TermsConditions";
import PrivacyPolicy from "./components/PrivacyPolicy";
import CancellationPolicy from "./components/CancellationPolicy";
import Travel from "./components/Travel";
import PartnerWithUs from "./components/PartnerWithUs";
import CorporateBookings from "./components/CorporateBookings";
import ReferEarn from "./components/ReferEarn";
import Membership from "./components/Membership";
import MembershipPlansPage from "./components/MembershipPlansPage";
import TravelGuides from "./components/TravelGuides";
import LoginForm from "./components/Login";
import Signup from "./components/Signup";
import EnhancedHotelBookingForm from "./components/EnhancedHotelBookingForm";
import HotelDetailPage from "./components/HotelDetailPage";
import RestaurantDetailPage from "./components/RestaurantDetailPage";
import BookingSearchPage from "./components/BookingSearchPage";
import QRBookingVerification from "./pages/QRBookingVerification";
import RoomBookingPage from "./pages/RoomBookingPage";
import RestaurantBookingPage from "./pages/RestaurantBookingPage";
import ProfilePage from "./components/ProfilePage";
import ProtectedRoute from "./components/ProtectedRoute";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import jwtTokenService from "./services/jwtTokenService";
import GoogleOneTap from "./components/GoogleOneTap";
import ScrollToTop from "./components/ScrollToTop";

function App() {
  const [mode, setMode] = useState("dark");
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = () => {
      const authStatus = jwtTokenService.isAuthenticated();
      setIsAuthenticated(authStatus);
    };

    checkAuth();

    // Listen for logout events
    const handleLogout = () => {
      setIsAuthenticated(false);
    };

    // Listen for login events
    const handleLogin = () => {
      setIsAuthenticated(true);
    };

    window.addEventListener("logout", handleLogout);
    window.addEventListener("login", handleLogin);

    // Check auth status periodically
    const interval = setInterval(checkAuth, 60000); // Every minute

    return () => {
      window.removeEventListener("logout", handleLogout);
      window.removeEventListener("login", handleLogin);
      clearInterval(interval);
    };
  }, []);

  // Disable browser automatic scroll restoration so our app can control scrolling
  useEffect(() => {
    try {
      if (typeof window !== "undefined" && 'scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
    } catch (e) {}
    return () => {
      try {
        if (typeof window !== "undefined" && 'scrollRestoration' in window.history) {
          window.history.scrollRestoration = 'auto';
        }
      } catch (e) {}
    };
  }, []);

  const toggleMode = () => {
    if (mode === "light") {
      setMode("dark");
      document.body.style.backgroundColor = "grey";
    } else {
      setMode("light");
      document.body.style.backgroundColor = "white";
    }
  };

  return (
    <ScrollLockProvider>
      <LanguageProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          <GoogleOneTap />
          <NavScrollExample mode={mode} toggleMode={toggleMode} />

          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginForm />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/" element={<BookingHomePage />} />
              <Route path="/restaurants" element={<RestaurantsPage />} />
              <Route path="/HotelRoomsPage" element={<HotelRoomsPage />} />
              <Route path="/hotel/:hotelId" element={<HotelDetailPage />} />
              <Route
                path="/restaurant/:restaurantId"
                element={<RestaurantDetailPage />}
              />
              <Route
                path="/room-booking/:roomId"
                element={
                  <ProtectedRoute>
                    <RoomBookingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/restaurant-booking/:restaurantId/:tableId"
                element={
                  <ProtectedRoute>
                    <RestaurantBookingPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/about_us" element={<AboutUs />} />
              <Route path="/Contact" element={<ContactUs />} />
              <Route path="/Faqs" element={<Faqs />} />
              <Route path="/Blog" element={<Blog />} />
              <Route path="/Careers" element={<Careers />} />
              <Route path="/CustomerSupport" element={<CustomerSupport />} />
              <Route path="/TermsConditions" element={<TermsConditions />} />
              <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
              <Route
                path="/CancellationPolicy"
                element={<CancellationPolicy />}
              />
              <Route path="/travel" element={<Travel />} />
              <Route path="/PartnerWithUs" element={<PartnerWithUs />} />
              <Route path="/CorporateBookings" element={<CorporateBookings />} />
              <Route path="/ReferEarn" element={<ReferEarn />} />
              <Route path="/Membership" element={<Membership />} />
              <Route path="/TravelGuides" element={<TravelGuides />} />
              <Route path="/Footer" element={<Footer />} />
              <Route path="/booking/verifybyqr" element={<QRBookingVerification />} />
              <Route path="/verify-booking/:pnr" element={<QRBookingVerification />} />

              {/* Protected Routes - Require Authentication */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/membership-plans"
                element={
                  <ProtectedRoute>
                    <MembershipPlansPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ProfileDropdown"
                element={
                  <ProtectedRoute>
                    <ProfileDropdown />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/search"
                element={
                  <ProtectedRoute>
                    <BookingSearchPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/enhanced-hotel-booking"
                element={
                  <ProtectedRoute>
                    <EnhancedHotelBookingForm />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
          <Footer />
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Router>
      </LanguageProvider>
    </ScrollLockProvider>
  );
}

export default App;
